import { CONVERSATION_CONSTANTS, AWAC_CONSTANTS } from "./constants";
import { stripPageTag } from "../awac/pageContext";

/**
 * Parses JSON data from a server-sent event.
 * @param {object} event - Server-sent event.
 * @returns {object} - Parsed server-sent event data.
 * @throws {Error} if event data is invalid.
 */
export function parseServerSentEventData(event) {
    if (event && event.data && typeof event.data === "string") {
        const data = JSON.parse(event.data);

        if (!data || typeof data !== "object") {
            throw new Error(`Error parsing data in server sent event.`);
        } else {
            return data;
        }
    } else {
        throw new Error(`Invalid data in server sent event.`);
    }
};

/**
 * Get the sender's display name from incoming typing started/stopped indicator events.
 * @param {Object} data - Data from typing indicator server-sent events.
 * @returns {String} - Parsed display name of sender.
 */
export function getSenderDisplayName(data) {
    return (data && data.conversationEntry && data.conversationEntry.senderDisplayName) || "";
};

/**
 * Get the sender's role from incoming typing started/stopped indicator events.
 * @param {Object} data - Data from typing indicator server-sent events.
 * @returns {String} - Parsed role of the sender.
 */
export function getSenderRole(data) {
    return (data && data.conversationEntry && data.conversationEntry.sender && data.conversationEntry.sender.role) || "";
};

/**
 * Parses JSON entry payload field from a server-sent event data.
 * @param {object} data - Server-sent event.
 * @returns {object} - Parsed server-sent event data.
 * @throws {Error} if event data is invalid.
 */
// Friendly display name for the AI agent. Internal bot/deployment names
// (e.g. "Pilot Buyer Engagement V3") must never reach the prospect.
const AGENT_DISPLAY_NAME = 'Lia';

/**
 * Prospect-facing display name for a conversation actor: the bot — and any
 * internal-looking name (deployment/bot naming like "Pilot Buyer Engagement
 * V3") — is presented as Lia. A human participant with a real name keeps it.
 */
export function friendlyActorName(rawName, role) {
    if (role === CONVERSATION_CONSTANTS.ParticipantRoles.CHATBOT
        || !rawName
        || /pilot|agent|bot/i.test(rawName)) {
        return AGENT_DISPLAY_NAME;
    }
    return rawName;
}

export function createConversationEntry(data) {
    try {
        if (typeof data === "object") {
            const entryPayload = JSON.parse(data.conversationEntry.entryPayload);

            // Do not create a conversation-entry for unknown/unsupported entryType.
            if (!Object.values(CONVERSATION_CONSTANTS.EntryTypes).includes(entryPayload.entryType)) {
                console.warn(`Unexpected and/or unsupported entryType: ${entryPayload.entryType}`);
                return;
            }

            const role = data.conversationEntry.sender.role;
            const rawActorName = data.conversationEntry.senderDisplayName ? (data.conversationEntry.senderDisplayName || role) : (entryPayload.entries[0].displayName || entryPayload.entries[0].participant.role);

            return {
                conversationId: data.conversationId,
                messageId: data.conversationEntry.identifier,
                content: entryPayload.abstractMessage || entryPayload,
                messageType: entryPayload.abstractMessage ? entryPayload.abstractMessage.messageType : (entryPayload.routingType || entryPayload.entries[0].operation) ,
                entryType: entryPayload.entryType,
                sender: data.conversationEntry.sender,
                // The end user keeps their own name; every bot/agent actor is "Lia".
                actorName: role === CONVERSATION_CONSTANTS.ParticipantRoles.ENDUSER
                    ? rawActorName
                    : friendlyActorName(rawActorName, role),
                actorType: role,
                transcriptedTimestamp: data.conversationEntry.transcriptedTimestamp,
                messageReason: entryPayload.messageReason
            };
        } else {
            throw new Error(`Expected an object to create a new conversation entry but instead, received ${data}`);
        }
    } catch (err) {
        throw new Error(`Something went wrong while creating a conversation entry: ${err}`);
    }
    
};

//============================================================== STATIC TEXT MESSAGE functions ==============================================================
/**
 * Validates whether the supplied object is a conversation-entry with entry type as CONVERSATION_MESSAGE.
 * @param {object} conversationEntry
 * @returns {boolean} - TRUE - if the conversation-entry is a CONVERSATION_MESSAGE and FALSE - otherwise.
 */
export function isConversationEntryMessage(conversationEntry) {
    if (conversationEntry) {
        return conversationEntry.entryType === CONVERSATION_CONSTANTS.EntryTypes.CONVERSATION_MESSAGE;
    }
    return false;
};

/**
 * Validates whether the supplied CONVERSATION_MESSAGE is originating from an end-user participant.
 * @param {object} conversationEntry
 * @returns {boolean} - TRUE - if the CONVERSATION_MESSAGE is sent by the end-user participant and FALSE - otherwise.
 */
export function isMessageFromEndUser(conversationEntry) {
    if (isConversationEntryMessage(conversationEntry)) {
        return conversationEntry.actorType === CONVERSATION_CONSTANTS.ParticipantRoles.ENDUSER;
    }
    return false;
};

/**
 * Validates whether the supplied CONVERSATION_MESSAGE is a STATIC_CONTENT_MESSAGE (i.e. messageType === "STATIC_CONTENT_MESSAGE").
 * @param {object} conversationEntry
 * @returns {boolean} - TRUE - if the CONVERSATION_MESSAGE is a STATIC_CONTENT_MESSAGE and FALSE - otherwise.
 */
export function isConversationEntryStaticContentMessage(conversationEntry) {
    if (isConversationEntryMessage(conversationEntry)) {
        return conversationEntry.content && conversationEntry.content.messageType === CONVERSATION_CONSTANTS.MessageTypes.STATIC_CONTENT_MESSAGE;
    }
    return false;
};

/**
 * Gets the supplied STATIC_CONTENT_MESSAGE's payload.
 * @param {object} conversationEntry
 * @returns {object|undefined}
 */
export function getStaticContentPayload(conversationEntry) {
    if (isConversationEntryStaticContentMessage(conversationEntry)) {
        return conversationEntry.content && conversationEntry.content.staticContent;
    }
    return undefined;
};

/**
 * Validates whether the supplied STATIC_CONTENT_MESSAGE is a Text Message (i.e. formatType === "Text").
 * NOTE: AWAC envelope messages are also Text format, but they must NOT render as a plain text
 * bubble — so a true text message is one that is Text format AND not an AWAC envelope.
 * @param {object} conversationEntry
 * @returns {boolean} - TRUE - if the STATIC_CONTENT_MESSAGE is a plain Text Message and FALSE - otherwise.
 */
export function isTextMessage(conversationEntry) {
    if (isConversationEntryStaticContentMessage(conversationEntry)) {
        const isText = getStaticContentPayload(conversationEntry).formatType === CONVERSATION_CONSTANTS.FormatTypes.TEXT;
        return isText && !isAwacMessage(conversationEntry);
    }
};

//============================================================== AWAC (Adaptive Web) functions ==============================================================
/**
 * Attempts to parse the AWAC JSON envelope out of a Text message's content.
 * The agent sends a StaticContentMessage (formatType=Text) whose text is a JSON
 * envelope: { "awac": 1, "template": [{ "name": "FloorPlanShowcase" }], "text": ..., "curation": [...], "options": [...] }.
 * Returns the parsed envelope object if valid, otherwise undefined.
 * @param {object} conversationEntry
 * @returns {object|undefined}
 */
export function getAwacEnvelope(conversationEntry) {
    if (!isConversationEntryStaticContentMessage(conversationEntry)) {
        return undefined;
    }
    const payload = getStaticContentPayload(conversationEntry);
    if (!payload || payload.formatType !== CONVERSATION_CONSTANTS.FormatTypes.TEXT) {
        return undefined;
    }
    const raw = typeof payload.text === "string" ? payload.text.trim() : "";
    // Cheap pre-check before attempting a JSON parse: must look like an object containing the marker.
    if (!raw.startsWith("{") || raw.indexOf(`"${AWAC_CONSTANTS.ENVELOPE_MARKER}"`) === -1) {
        return undefined;
    }
    try {
        const envelope = JSON.parse(raw);
        if (envelope && typeof envelope === "object" && envelope[AWAC_CONSTANTS.ENVELOPE_MARKER]) {
            // Version tolerance: accept any schema version up to the client's
            // declared max. A future version above the max falls back to the
            // plain-text bubble instead of risking a broken render.
            const version = Number(envelope[AWAC_CONSTANTS.ENVELOPE_MARKER]);
            if (Number.isFinite(version) && version > AWAC_CONSTANTS.ENVELOPE_MAX_SUPPORTED_VERSION) {
                return undefined;
            }
            return envelope;
        }
    } catch (e) {
        // Not valid JSON — treat as a normal text message (graceful fallback).
        return undefined;
    }
    return undefined;
};

/**
 * Validates whether the supplied conversation-entry is an AWAC envelope message.
 * @param {object} conversationEntry
 * @returns {boolean}
 */
export function isAwacMessage(conversationEntry) {
    return getAwacEnvelope(conversationEntry) !== undefined;
};

/**
 * Resolves the AWAC template name the agent asked the client to render.
 * @param {object} conversationEntry
 * @returns {string|undefined}
 */
export function getAwacTemplateName(conversationEntry) {
    const envelope = getAwacEnvelope(conversationEntry);
    if (!envelope) {
        return undefined;
    }
    if (Array.isArray(envelope.template) && envelope.template[0] && envelope.template[0].name) {
        return envelope.template[0].name;
    }
    // Allow a flat { component: "FloorPlanShowcase" } shorthand too.
    return envelope.component || undefined;
};

/**
 * Gets the supplied Text Message's text.
 * @param {object} conversationEntry
 * @returns {string}
 */
export function getTextMessageContent(conversationEntry) {
    if (isTextMessage(conversationEntry)) {
        // Hide the navigation-context suffix the client appends to outgoing
        // messages (see awac/pageContext.js) — it's for the agent, not humans.
        return stripPageTag(getStaticContentPayload(conversationEntry).text);
    }
    return "";
};

//============================================================== CHOICES MESSAGE functions ==============================================================
/**
 * Validates whether the supplied CONVERSATION_MESSAGE is a CHOICES_MESSAGE (i.e. messageType === "ChoicesMessage").
 * @param {object} conversationEntry
 * @returns {boolean} - TRUE - if the CONVERSATION_MESSAGE is a CHOICES_MESSAGE and FALSE - otherwise.
 */
export function isConversationEntryChoicesMessage(conversationEntry) {
    if (isConversationEntryMessage(conversationEntry)) {
        return conversationEntry.content && conversationEntry.content.messageType === CONVERSATION_CONSTANTS.MessageTypes.CHOICES_MESSAGE;
    }
    return false;
};

/**
 * Validates whether the supplied CHOICES_MESSAGE is QUICK_REPLIES (i.e. formatType === "QuickReplies") or BUTTONS (i.e. formatType === "Buttons").
 * @param {object} conversationEntry
 * @returns {boolean} - TRUE - if the CHOICES_MESSAGE is a QUICK_REPLIES or BUTTONS format type and FALSE - otherwise.
 */
export function isChoicesMessage(conversationEntry) {
    if (isConversationEntryChoicesMessage(conversationEntry)) {
        return getStaticContentPayload(conversationEntry).formatType === CONVERSATION_CONSTANTS.FormatTypes.QUICK_REPLIES 
            || getStaticContentPayload(conversationEntry).formatType === CONVERSATION_CONSTANTS.FormatTypes.BUTTONS;
    }
    return false;
};

//============================================================== PARTICIPANT CHANGE functions ==============================================================
/**
 * Validates whether the supplied object is a conversation-entry with entry type as PARTICIPANT_CHANGED.
 * @param {object} conversationEntry
 * @returns {boolean} - TRUE - if the conversation-entry is a PARTICIPANT_CHANGED event and FALSE - otherwise.
 */
export function isParticipantChangeEvent(conversationEntry) {
    return conversationEntry.entryType === CONVERSATION_CONSTANTS.EntryTypes.PARTICIPANT_CHANGED;
};

/**
 * Validates whether the supplied PARTICIPANT_CHANGED conversation-entry's participant joined the conversation. 
 * @param {object} conversationEntry
 * @returns {boolean} - TRUE - if the participant joined and FALSE - if the participant left.
 */
export function hasParticipantJoined(conversationEntry) {
    return isParticipantChangeEvent(conversationEntry) && conversationEntry.content.entries[0].operation === CONVERSATION_CONSTANTS.ParticipantChangedOperations.ADD;
};

/**
 * Gets the supplied PARTICIPANT_CHANGED conversation-entry's participant name.
 * @param {object} conversationEntry
 * @returns {string}
 */
export function getParticipantChangeEventPartcipantName(conversationEntry) {
    return isParticipantChangeEvent(conversationEntry) && (conversationEntry.content.entries[0].displayName || conversationEntry.content.entries[0].participant.role);
};